import React, { useState, useEffect } from "react";

const monthName = (monthNumberStartingFromZero) => [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
][monthNumberStartingFromZero];

// Build arrays for day, month, and year
const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);
const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i,
  label: monthName(i),
}));
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear - 1940 + 1 }, (_, i) => currentYear - i);

function BirthDateSelect({ birth_date, onChange }) {
  // Separate states for month, day, and year, all start as an empty string
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [error, setError] = useState("");

  // If a `birth_date` prop is passed in (string "YYYY-MM-DD" or a Date), parse it
  useEffect(() => {
    if (!birth_date) return;

    // If birth_date is a string in "YYYY-MM-DD", parse it
    if (typeof birth_date === "string") {
      const [rawYear, rawMonth, rawDay] = birth_date.split("-");
      const parsedYear = parseInt(rawYear, 10);
      const parsedMonth = parseInt(rawMonth, 10) - 1; // zero-based
      const parsedDay = parseInt(rawDay, 10);

      // Check for validity
      if (!isNaN(parsedYear) && !isNaN(parsedMonth) && !isNaN(parsedDay)) {
        setSelectedMonth(parsedMonth.toString());
        setSelectedDay(parsedDay.toString());
        setSelectedYear(parsedYear.toString());
      } else {
        setError("Invalid date format");
      }
    }
    // If it’s already a Date object
    else if (birth_date instanceof Date && !isNaN(birth_date.getTime())) {
      setSelectedMonth(birth_date.getMonth().toString());
      setSelectedDay(birth_date.getDate().toString());
      setSelectedYear(birth_date.getFullYear().toString());
    } else {
      setError("Invalid date");
    }
  }, [birth_date]);

  // Whenever the user changes month/day/year, try to build a valid Date
  // and call onChange if we have all three pieces
  const buildAndSendDate = (year, month, day) => {
    if (year && month !== "" && day) {
      const numericYear = parseInt(year, 10);
      const numericMonth = parseInt(month, 10);
      const numericDay = parseInt(day, 10);

      // Quick check if user tried something invalid
      // e.g., 31st of February, etc.
      const candidate = new Date(numericYear, numericMonth, numericDay);

      if (!isNaN(candidate.getTime()) &&
          candidate.getFullYear() === numericYear &&
          candidate.getMonth() === numericMonth &&
          candidate.getDate() === numericDay
      ) {
        setError("");
        onChange?.(candidate);
      } else {
        setError("Invalid date selected.");
      }
    }
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    buildAndSendDate(selectedYear, newMonth, selectedDay);
  };

  const handleDayChange = (e) => {
    const newDay = e.target.value;
    setSelectedDay(newDay);
    buildAndSendDate(selectedYear, selectedMonth, newDay);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    buildAndSendDate(newYear, selectedMonth, selectedDay);
  };

  return (
    <div>
      <label>Birthdate</label>
      {error && <div style={{ color: "red" }}>{error}</div>}

      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <div>
          <label htmlFor="birth-month-select">Month</label>
          <select
            id="birth-month-select"
            className="field"
            aria-label="Select Month"
            value={selectedMonth}
            onChange={handleMonthChange}
          >
            <option value="" disabled>
              Select Month
            </option>
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="birth-day-select">Day</label>
          <select
            id="birth-day-select"
            className="field"
            aria-label="Select Day"
            value={selectedDay}
            onChange={handleDayChange}
          >
            <option value="" disabled>
              Select Day
            </option>
            {dayOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="birth-year-select">Year</label>
          <select
            id="birth-year-select"
            className="field"
            aria-label="Select Year"
            value={selectedYear}
            onChange={handleYearChange}
          >
            <option value="" disabled>
              Select Year
            </option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

export default BirthDateSelect;
